import type { ReactNode } from 'react'
import { Size } from '@front/model'
import { Group, Pagination, Select, Text, Tooltip } from '@mantine/core'
import { isEmpty, unique } from '@terros/common'
import type { TDataTableProps } from './types'

type PaginationProps<T extends Record<string, unknown> | unknown[]> = Pick<TDataTableProps<T>, 'pagination'>

const DEFAULT_PAGE_SIZES = [10, 20, 30, 40, 50, 100]

export const TDataTablePagination = <T extends Record<string, unknown> | unknown[]>({
  pagination,
}: PaginationProps<T>): ReactNode => {
  if (!pagination) return
  const { currentPage, totalRecords, recordsPerPage, recordsPerPageOptions, onRecordsPerPageChange } = pagination

  const from = currentPage * recordsPerPage - recordsPerPage + 1
  const to = Math.min(currentPage * recordsPerPage, totalRecords)
  const pages = Math.ceil(totalRecords / recordsPerPage)

  const options = recordsPerPageOptions ?? unique(DEFAULT_PAGE_SIZES.concat(recordsPerPage)).sort((a, b) => a - b)
  return (
    <Group p={Size.space.xl} style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
      <Tooltip label='Select Records per page'>
        <Select
          data={options.map((value) => ({ value: String(value), label: String(value) }))}
          disabled={!onRecordsPerPageChange || isEmpty(options) || options.length === 1}
          size='xs'
          value={String(recordsPerPage)}
          w={70}
          onChange={(e) => {
            if (onRecordsPerPageChange && e) {
              onRecordsPerPageChange(parseInt(e))
            }
          }}
        />
      </Tooltip>
      <Pagination size='sm' total={pages} value={pagination.currentPage} onChange={pagination.onCurrentPageChange} />
      <Text>
        {from} - {to} / {totalRecords}
      </Text>
    </Group>
  )
}
